/** TanStack Query hooks over the typed client. One place for query keys and invalidation. */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type {
  AlertInput,
  Connection,
  Dashboard,
  DestinationTypeId,
  Granularity,
  RangeKey,
  ReportInput,
  RequestStatus,
  SeriesRange,
  User,
} from './types';

export const qk = {
  me: ['me'] as const,
  integrations: ['integrations'] as const,
  integration: (id: string) => ['integrations', id] as const,
  connections: ['connections'] as const,
  connection: (id: string) => ['connections', id] as const,
  accounts: (id: string) => ['connections', id, 'accounts'] as const,
  connectionKpis: (id: string) => ['connections', id, 'kpis'] as const,
  dashboard: (range: RangeKey) => ['dashboard', range] as const,
  dashboardAll: ['dashboard'] as const,
  widgets: ['widgets'] as const,
  series: (id: string, range: SeriesRange, granularity: Granularity) => ['series', id, range, granularity] as const,
  requests: ['requests'] as const,
  destinationTypes: ['destination-types'] as const,
  destinations: ['destinations'] as const,
  reports: ['reports'] as const,
  reportPreview: (id: string) => ['reports', id, 'preview'] as const,
  alerts: ['alerts'] as const,
  alertEvents: ['alerts', 'events'] as const,
  adminSummary: ['admin', 'summary'] as const,
  adminRequests: (kind?: string) => ['admin', 'requests', kind ?? 'all'] as const,
  adminStats: ['admin', 'stats'] as const,
};

// ------------------------------------------------------------------ me

export function useMe(enabled = true) {
  return useQuery({ queryKey: qk.me, queryFn: () => api.get('/v1/me'), enabled });
}

export function useUpdateMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name?: string; timezone?: string; currency?: string }) => api.patch('/v1/me', { body }),
    onSuccess: (user: User) => {
      qc.setQueryData(qk.me, user);
      void qc.invalidateQueries({ queryKey: qk.dashboardAll });
    },
  });
}

// ------------------------------------------------------------------ catalog

export function useIntegrations() {
  return useQuery({ queryKey: qk.integrations, queryFn: () => api.get('/v1/integrations'), staleTime: 5 * 60_000 });
}

// ------------------------------------------------------------------ connections

const hasSyncing = (list: Connection[] | undefined) =>
  !!list?.some((c) => c.status === 'syncing' || c.status === 'pending');

export function useConnections() {
  return useQuery({
    queryKey: qk.connections,
    queryFn: async () => (await api.get('/v1/connections')).connections,
    refetchInterval: (q) => (hasSyncing(q.state.data) ? 3000 : false),
  });
}

export function useConnection(id: string | undefined) {
  return useQuery({
    queryKey: qk.connection(id ?? ''),
    queryFn: () => api.get('/v1/connections/{connection_id}', { path: { connection_id: id! } }),
    enabled: !!id,
  });
}

export function useAccounts(id: string | undefined) {
  return useQuery({
    queryKey: qk.accounts(id ?? ''),
    queryFn: async () => (await api.get('/v1/connections/{connection_id}/accounts', { path: { connection_id: id! } })).accounts,
    enabled: !!id,
  });
}

export function useConnectionKpis(id: string | undefined) {
  return useQuery({
    queryKey: qk.connectionKpis(id ?? ''),
    queryFn: async () => (await api.get('/v1/connections/{connection_id}/kpis', { path: { connection_id: id! } })).kpis,
    enabled: !!id,
  });
}

function useInvalidateData() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: qk.connections });
    void qc.invalidateQueries({ queryKey: qk.dashboardAll });
    void qc.invalidateQueries({ queryKey: qk.widgets });
    void qc.invalidateQueries({ queryKey: qk.integrations });
  };
}

export function useChooseAccount(connectionId: string) {
  const invalidate = useInvalidateData();
  return useMutation({
    mutationFn: (external_account_id: string) =>
      api.put('/v1/connections/{connection_id}/accounts', { path: { connection_id: connectionId }, body: { external_account_id } }),
    onSuccess: invalidate,
  });
}

export function useUpdateConnection() {
  const invalidate = useInvalidateData();
  return useMutation({
    mutationFn: (v: { id: string; display_name?: string; fields?: Record<string, string> }) =>
      api.patch('/v1/connections/{connection_id}', {
        path: { connection_id: v.id },
        body: { display_name: v.display_name, fields: v.fields },
      }),
    onSuccess: invalidate,
  });
}

export function useDeleteConnection() {
  const invalidate = useInvalidateData();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete('/v1/connections/{connection_id}', { path: { connection_id: id } }),
    onSuccess: () => {
      invalidate();
      void qc.invalidateQueries({ queryKey: qk.alerts });
      void qc.invalidateQueries({ queryKey: qk.reports });
    },
  });
}

export function useSyncConnection() {
  const invalidate = useInvalidateData();
  return useMutation({
    mutationFn: (id: string) => api.post('/v1/connections/{connection_id}/sync', { path: { connection_id: id } }),
    onSettled: invalidate,
  });
}

// ------------------------------------------------------------------ dashboard & widgets

export function useDashboard(range: RangeKey) {
  return useQuery({
    queryKey: qk.dashboard(range),
    queryFn: ({ signal }) => api.get('/v1/dashboard', { query: { range }, signal }),
    placeholderData: keepPreviousData,
    refetchInterval: (q) => (q.state.data?.widgets.some((w) => w.status === 'syncing') ? 4000 : false),
  });
}

export function useWidgets() {
  return useQuery({ queryKey: qk.widgets, queryFn: async () => (await api.get('/v1/widgets')).widgets });
}

export function useAddWidgets() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items: { connection_id: string; kpi_key: string }[]) => api.post('/v1/widgets', { body: { items } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.dashboardAll });
      void qc.invalidateQueries({ queryKey: qk.widgets });
      void qc.invalidateQueries({ queryKey: ['connections'] });
    },
  });
}

export function useRemoveWidget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete('/v1/widgets/{widget_id}', { path: { widget_id: id } }),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: qk.dashboardAll });
      qc.setQueriesData<Dashboard>({ queryKey: qk.dashboardAll }, (d) =>
        d ? { ...d, widgets: d.widgets.filter((w) => w.id !== id) } : d,
      );
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: qk.dashboardAll });
      void qc.invalidateQueries({ queryKey: qk.widgets });
      void qc.invalidateQueries({ queryKey: ['connections'] });
      void qc.invalidateQueries({ queryKey: qk.alerts });
    },
  });
}

/** PUT /v1/widgets/order with every widget id once; the dashboard cache is reordered optimistically. */
export function useReorderWidgets() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (visible: string[]) => {
      // The API wants every widget exactly once. Widgets can appear server-side while the user
      // drags (default KPIs land after a first sync), so merge with the current layout first.
      const current = (await api.get('/v1/widgets')).widgets.map((w) => w.id);
      const widget_ids = [...visible.filter((id) => current.includes(id)), ...current.filter((id) => !visible.includes(id))];
      await api.put('/v1/widgets/order', { body: { widget_ids } });
    },
    onMutate: async (ids) => {
      await qc.cancelQueries({ queryKey: qk.dashboardAll });
      const snapshot = qc.getQueriesData<Dashboard>({ queryKey: qk.dashboardAll });
      const index = new Map(ids.map((id, i) => [id, i]));
      qc.setQueriesData<Dashboard>({ queryKey: qk.dashboardAll }, (d) =>
        d
          ? {
              ...d,
              widgets: [...d.widgets]
                .sort((a, b) => (index.get(a.id) ?? 0) - (index.get(b.id) ?? 0))
                .map((w, i) => ({ ...w, position: i })),
            }
          : d,
      );
      return { snapshot };
    },
    onError: (_e, _ids, ctx) => {
      for (const [key, data] of ctx?.snapshot ?? []) qc.setQueryData(key, data);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: qk.dashboardAll });
      void qc.invalidateQueries({ queryKey: qk.widgets });
    },
  });
}

export function useSeries(id: string | undefined, range: SeriesRange, granularity: Granularity) {
  return useQuery({
    queryKey: qk.series(id ?? '', range, granularity),
    queryFn: ({ signal }) =>
      api.get('/v1/widgets/{widget_id}/series', { path: { widget_id: id! }, query: { range, granularity }, signal }),
    enabled: !!id,
    placeholderData: keepPreviousData,
  });
}

// ------------------------------------------------------------------ requests

export function useMyRequests() {
  return useQuery({ queryKey: qk.requests, queryFn: async () => (await api.get('/v1/requests')).requests });
}

export function useRequestIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      integration_id?: string | null;
      website?: string | null;
      kpis_wanted?: string | null;
      notes?: string | null;
    }) => api.post('/v1/requests/integration', { body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.requests }),
  });
}

export function useRequestCustomDashboard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { description: string; company?: string | null; phone?: string | null; integrations?: string[] }) =>
      api.post('/v1/requests/custom-dashboard', { body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.requests }),
  });
}

// ------------------------------------------------------------------ destinations

export function useDestinationTypes() {
  return useQuery({
    queryKey: qk.destinationTypes,
    queryFn: async () => (await api.get('/v1/destination-types')).types,
    staleTime: 10 * 60_000,
  });
}

export function useDestinations(opts: { poll?: boolean } = {}) {
  return useQuery({
    queryKey: qk.destinations,
    queryFn: async () => (await api.get('/v1/destinations')).destinations,
    refetchInterval: opts.poll ? 3000 : false,
  });
}

export function useCreateDestination() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { type: DestinationTypeId; label?: string | null; config: Record<string, string> }) =>
      api.post('/v1/destinations', { body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.destinations }),
  });
}

export function useVerifyDestination() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; code: string }) =>
      api.post('/v1/destinations/{destination_id}/verify', { path: { destination_id: v.id }, body: { code: v.code } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.destinations }),
  });
}

export function useTestDestination() {
  return useMutation({
    mutationFn: (id: string) => api.post('/v1/destinations/{destination_id}/test', { path: { destination_id: id } }),
  });
}

export function useDeleteDestination() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete('/v1/destinations/{destination_id}', { path: { destination_id: id } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.destinations });
      void qc.invalidateQueries({ queryKey: qk.reports });
      void qc.invalidateQueries({ queryKey: qk.alerts });
    },
  });
}

// ------------------------------------------------------------------ reports

export function useReports() {
  return useQuery({ queryKey: qk.reports, queryFn: async () => (await api.get('/v1/reports')).reports });
}

export function useSaveReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id?: string; input: ReportInput }) =>
      v.id
        ? api.patch('/v1/reports/{report_id}', { path: { report_id: v.id }, body: v.input })
        : api.post('/v1/reports', { body: v.input }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.reports }),
  });
}

export function useDeleteReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete('/v1/reports/{report_id}', { path: { report_id: id } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.reports }),
  });
}

export function useReportPreview(id: string | undefined) {
  return useQuery({
    queryKey: qk.reportPreview(id ?? ''),
    queryFn: () => api.get('/v1/reports/{report_id}/preview', { path: { report_id: id! } }),
    enabled: !!id,
  });
}

export function useSendReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post('/v1/reports/{report_id}/send', { path: { report_id: id } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.reports }),
  });
}

// ------------------------------------------------------------------ alerts

export function useAlerts() {
  return useQuery({ queryKey: qk.alerts, queryFn: async () => (await api.get('/v1/alerts')).alerts });
}

export function useAlertEvents() {
  return useQuery({ queryKey: qk.alertEvents, queryFn: async () => (await api.get('/v1/alerts/events')).events });
}

export function useSaveAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id?: string; input: AlertInput }) =>
      v.id
        ? api.patch('/v1/alerts/{alert_id}', { path: { alert_id: v.id }, body: v.input })
        : api.post('/v1/alerts', { body: v.input }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.alerts }),
  });
}

export function useDeleteAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete('/v1/alerts/{alert_id}', { path: { alert_id: id } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.alerts }),
  });
}

// ------------------------------------------------------------------ admin

export function useAdminSummary() {
  return useQuery({ queryKey: qk.adminSummary, queryFn: async () => (await api.get('/v1/admin/requests/summary')).items });
}

export function useAdminRequests(kind?: 'integration' | 'custom_dashboard') {
  return useQuery({
    queryKey: qk.adminRequests(kind),
    queryFn: async () => (await api.get('/v1/admin/requests', { query: { kind } })).requests,
  });
}

export function useAdminStats() {
  return useQuery({ queryKey: qk.adminStats, queryFn: () => api.get('/v1/admin/stats') });
}

export function useSetRequestStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; status: RequestStatus }) =>
      api.patch('/v1/admin/requests/{request_id}', { path: { request_id: v.id }, body: { status: v.status } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin'] }),
  });
}
